import React from 'react'
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import { cn } from '@/utils/cn'

interface MobileDayNavigationProps {
  selectedDate: Date
  onPrevious: () => void
  onNext: () => void
  onToday: () => void
}

// Format date for display
function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

// Check if date is today
function isToday(date: Date): boolean {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

// Check if date is in the future
function isFutureDate(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const compareDate = new Date(date)
  compareDate.setHours(0, 0, 0, 0)
  return compareDate > today
}

/**
 * Day navigation bar for mobile sessions view
 * Shows current day with prev/next buttons and a "Today" quick action
 */
const MobileDayNavigation: React.FC<MobileDayNavigationProps> = ({
  selectedDate,
  onPrevious,
  onNext,
  onToday,
}) => {
  const todaySelected = isToday(selectedDate)
  const isFuture = isFutureDate(selectedDate)
  const isSunday = selectedDate.getDay() === 0

  return (
    <div className="bg-white border-b sticky top-16 z-40">
      <div className="px-4 py-3">
        {/* Navigation Row */}
        <div className="flex items-center justify-between">
          {/* Previous Day Button */}
          <button
            onClick={onPrevious}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            aria-label="Previous day"
          >
            <ChevronLeft className="h-6 w-6 text-gray-600" />
          </button>

          {/* Date Display */}
          <div className="flex flex-col items-center">
            <span
              className={cn(
                'text-lg font-semibold',
                todaySelected && 'text-primary-600',
                isSunday && 'text-gray-400',
                isFuture && !todaySelected && 'text-gray-500'
              )}
            >
              {formatDisplayDate(selectedDate)}
            </span>
            {todaySelected && (
              <span className="text-xs text-primary-500 font-medium">Today</span>
            )}
            {isSunday && !todaySelected && (
              <span className="text-xs text-gray-400">Closed</span>
            )}
          </div>

          {/* Next Day Button */}
          <button
            onClick={onNext}
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            aria-label="Next day"
          >
            <ChevronRight className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Today Button (shown when not on today) */}
        {!todaySelected && (
          <div className="mt-2 flex justify-center">
            <button
              onClick={onToday}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
                'bg-primary-50 text-primary-600',
                'hover:bg-primary-100 active:bg-primary-200',
                'transition-colors'
              )}
            >
              <Calendar className="h-4 w-4" />
              Go to Today
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileDayNavigation
